/**
 * Cart upsell ("You'll love these").
 *
 * Shows a horizontally scrollable row of product suggestions inside the cart
 * drawer, based on what is in the cart. Cards come from Shopify's product
 * recommendations (rendered by sections/cart-upsell.liquid). Clicking ADD puts
 * the chosen variant in the cart and refreshes the drawer.
 */

const PROTECTION_HANDLE = 'shipping-protection';
const MAX_SEEDS = 2;
const MAX_CARDS = 10;
const REFRESH_DELAY = 450;

/** @type {null | ((sectionId: string, html: string, options?: object) => unknown)} */
let morphSection = null;

import('@theme/section-renderer')
  .then((module) => {
    morphSection = module.morphSection;
  })
  .catch((error) => {
    console.warn('[cart-upsell] section-renderer not available:', error);
  });

function routeRoot() {
  const shopify = window.Shopify;
  return shopify && shopify.routes && shopify.routes.root ? shopify.routes.root : '/';
}

class CartUpsell extends HTMLElement {
  #listHtml = '';
  #loading = false;
  #again = false;
  #timer = 0;
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener('cart:updated', this.#onCartChange);

    // If the theme re-renders the drawer and wipes our cards, put them back.
    this.#observer = new MutationObserver(() => this.#restoreIfWiped());
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });

    this.refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('cart:updated', this.#onCartChange);
    if (this.#observer) this.#observer.disconnect();
    window.clearTimeout(this.#timer);
  }

  async refresh() {
    if (this.#loading) {
      this.#again = true;
      return;
    }

    this.#loading = true;

    try {
      const root = routeRoot();
      const cartResponse = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      const realItems = cart.items.filter((item) => item.handle !== PROTECTION_HANDLE);

      if (!realItems.length) {
        this.#clear();
        return;
      }

      const inCart = new Set(cart.items.map((item) => String(item.product_id)));
      const sectionId = this.dataset.sectionId || 'cart-upsell';
      const seeds = realItems.slice(0, MAX_SEEDS);

      const responses = await Promise.all(
        seeds.map((item) =>
          fetch(
            `${root}recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${item.product_id}&limit=${MAX_CARDS}&intent=related`,
            { credentials: 'same-origin' }
          )
            .then((response) => (response.ok ? response.text() : ''))
            .catch(() => '')
        )
      );

      const cards = [];
      const seen = new Set();

      for (const html of responses) {
        if (!html) continue;

        const doc = new DOMParser().parseFromString(html, 'text/html');

        doc.querySelectorAll('.cart-upsell__card').forEach((card) => {
          const id = card.getAttribute('data-product-id');
          if (!id || seen.has(id) || inCart.has(id)) return;
          seen.add(id);
          cards.push(card.outerHTML);
        });
      }

      if (!cards.length) {
        this.#clear();
        return;
      }

      this.#show(
        `<div class="cart-upsell__list" data-cart-upsell-list>${cards.slice(0, MAX_CARDS).join('')}</div>`
      );
    } catch (error) {
      console.warn('[cart-upsell] Could not load suggestions:', error);
    } finally {
      this.#loading = false;

      if (this.#again) {
        this.#again = false;
        this.refresh();
      }
    }
  }

  #show(html) {
    const body = this.querySelector('.cart-upsell__body');
    if (!body) return;

    this.#listHtml = html;
    body.innerHTML = html;
    this.hidden = false;
  }

  #clear() {
    const body = this.querySelector('.cart-upsell__body');

    this.#listHtml = '';
    if (body) body.innerHTML = '';
    this.hidden = true;
  }

  #restoreIfWiped() {
    if (!this.#listHtml) return;

    const body = this.querySelector('.cart-upsell__body');
    if (!body) return;

    if (!body.querySelector('[data-cart-upsell-list]')) {
      body.innerHTML = this.#listHtml;
    }

    if (this.hidden) this.hidden = false;
  }

  #syncHtml() {
    const list = this.querySelector('[data-cart-upsell-list]');

    if (list && list.querySelector('.cart-upsell__card')) {
      this.#listHtml = list.outerHTML;
    } else {
      this.#clear();
    }
  }

  #onCartChange = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.refresh(), REFRESH_DELAY);
  };

  #onClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-upsell-add]');
    if (!button || button.disabled) return;

    const card = button.closest('.cart-upsell__card');
    const field = card ? card.querySelector('[data-cart-upsell-variant]') : null;
    const variantId = field ? Number(field.value) : 0;
    if (!variantId) return;

    const cartSectionId = this.dataset.cartSectionId || 'cart-drawer-section';
    const label = button.textContent;

    button.disabled = true;
    button.textContent = '...';

    try {
      const response = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: cartSectionId,
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add this item');
      }

      const sectionHtml = result.sections ? result.sections[cartSectionId] : null;

      if (sectionHtml && morphSection) {
        await morphSection(cartSectionId, sectionHtml, { mode: 'hydration' });
      } else {
        window.location.reload();
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: {} }));

      if (card) card.remove();
      this.#syncHtml();
    } catch (error) {
      console.warn('[cart-upsell]', error);
      button.textContent = 'Not available';

      window.setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
      }, 2000);

      return;
    }

    button.disabled = false;
    button.textContent = label;
  };
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}